import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { PlayerController } from '../player/PlayerController.js';
import { ISLAND_RADIUS, SPAWN_CONFIG } from '../terrain/TerrainConstants.js';
import { TerrainCollisionManager } from '../collision/TerrainCollisionManager.js';
import { PlayerModel } from './PlayerModel.js';

export class PlayerSpawner {
    constructor(world, chunkManager, scene) {
        this.world = world;
        this.chunkManager = chunkManager;
        this.scene = scene;
        this.collisionManager = TerrainCollisionManager.getInstance();
    }

    calculateSpawnPoint() {
        // Generate random angle and distance within constraints
        const angle = Math.random() * Math.PI * 2;
        const distance = ISLAND_RADIUS * (
            SPAWN_CONFIG.RANDOM_CONSTRAINTS.MIN_DISTANCE_FROM_CENTER +
            Math.random() * (SPAWN_CONFIG.RANDOM_CONSTRAINTS.MAX_DISTANCE_FROM_CENTER - 
                           SPAWN_CONFIG.RANDOM_CONSTRAINTS.MIN_DISTANCE_FROM_CENTER)
        );

        // Calculate spawn coordinates relative to world center
        const worldCenter = this.chunkManager.worldSize / 2;
        const spawnX = worldCenter + Math.cos(angle) * distance;
        const spawnZ = worldCenter + Math.sin(angle) * distance;
        
        // Get the collision height at spawn point
        const collisionHeight = this.collisionManager.getCollisionHeight(spawnX, spawnZ);
        
        console.log(`Spawn coordinates: x=${spawnX}, y=${collisionHeight + 2}, z=${spawnZ}`);
        
        // Return the spawn point with correct height
        return new THREE.Vector3(
            spawnX,
            collisionHeight + 2, // 2 blocks above collision height
            spawnZ
        );
    }

    spawnPlayer() {
        try {
            const spawnPoint = this.calculateSpawnPoint();
            
            console.log("Calculated spawn point:", spawnPoint);
            
            // Create the player model with correct scale to make it 2 blocks high, 1 wide
            const playerModel = new PlayerModel({
                scale: 0.6, // Reduced scale to ensure 2x1 size
                animationSpeed: 1.2
            });
            
            // Position with safety checks
            playerModel.position.copy(spawnPoint);
            
            // Add to scene after setting position to ensure it's preserved
            this.scene.add(playerModel);
            
            // Set shadows for all meshes in the player model
            playerModel.traverse(child => {
                if (child instanceof THREE.Mesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                }
            });
            
            // Add a marker light above player for visibility
            const light = new THREE.PointLight(0xffffff, 0.5, 20);
            light.position.set(0, 3, 0); // Adjusted height
            playerModel.add(light);

            // Physics body - using 2x1 dimensions for proper collision
            const shape = new CANNON.Box(new CANNON.Vec3(0.5, 1.0, 0.5)); // 1x2x1 dimensions
            const playerBody = new CANNON.Body({
                mass: 5,
                position: new CANNON.Vec3(spawnPoint.x, spawnPoint.y, spawnPoint.z),
                shape: shape,
                fixedRotation: true,
                type: CANNON.Body.KINEMATIC
            });
            
            // Increase collision margin
            playerBody.shapes.forEach(shape => {
                shape.collisionFilterGroup = 1;  // Player group
                shape.collisionFilterMask = 1;   // Can collide with terrain
            });
            
            this.world.addBody(playerBody);
            
            console.log("Player spawned at:", playerModel.position);
            
            // Debug output of player dimensions
            const bbox = new THREE.Box3().setFromObject(playerModel);
            const size = new THREE.Vector3();
            bbox.getSize(size);
            console.log("Player model dimensions:", size);
            
            return { mesh: playerModel, body: playerBody, model: playerModel };
        } catch (e) {
            console.error("Critical error in player spawning:", e);
            // Fallback to the old box mesh as a safety measure
            const playerMesh = new THREE.Mesh(
                new THREE.BoxGeometry(1, 2, 1), // Explicit 2x1 box
                new THREE.MeshStandardMaterial({ color: 0xff0000 })
            );
            playerMesh.position.set(256, 50, 256); // Center of world, very high up
            this.scene.add(playerMesh);
            
            const shape = new CANNON.Box(new CANNON.Vec3(0.5, 1.0, 0.5)); // 1x2x1 dimensions
            const playerBody = new CANNON.Body({
                mass: 5,
                position: new CANNON.Vec3(256, 50, 256),
                shape,
                fixedRotation: true,
                type: CANNON.Body.KINEMATIC
            });
            this.world.addBody(playerBody);
            
            return { mesh: playerMesh, body: playerBody };
        }
    }
}
